import {
  CLIENT_INTAKE_SUBMITTED_BODY,
  CLIENT_INTAKE_SUBMITTED_HEADLINE,
  PRIMARY_EVIDENCE_TYPE_OPTIONS,
  toPrimaryEvidenceTypeLabel,
} from "@enrein/shared-contracts/display/tier1-client-display";
import type { PrimaryEvidenceType } from "@enrein/shared-contracts";
import type { ClientPortalApiClient } from "./api.js";
import { useIntakeForm } from "./useIntakeForm.js";

export function IntakePanel({
  api,
  clientLinkId,
  token,
  linkStatus,
  onStatus,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
  readonly linkStatus: string;
  readonly onStatus: (message: string | null) => void;
}) {
  const intake = useIntakeForm({ api, clientLinkId, token, linkStatus, onStatus });

  if (intake.error) {
    return (
      <section className="panel value-card" aria-label="Business intake">
        <p className="value-card-eyebrow">Getting started</p>
        <h2 className="value-card-headline">Business intake</h2>
        <p className="warning">{intake.error}</p>
      </section>
    );
  }

  if (!intake.view) {
    return (
      <section className="panel value-card enrein-loading-line" aria-busy="true" aria-label="Business intake">
        <p className="value-card-eyebrow">Getting started</p>
        <h2 className="value-card-headline">Business intake</h2>
        <p>Loading your intake form…</p>
      </section>
    );
  }

  if (intake.readOnly) {
    return (
      <section className="panel value-card panel--intake-submitted" aria-label="Business intake">
        <p className="value-card-eyebrow">Getting started</p>
        <h2 className="value-card-headline">{CLIENT_INTAKE_SUBMITTED_HEADLINE}</h2>
        <div className="intake-submitted-banner" role="status">
          <strong>Submitted to your agency</strong>
          <p>{CLIENT_INTAKE_SUBMITTED_BODY}</p>
        </div>
        <dl className="intake-summary">
          <div>
            <dt>Business name</dt>
            <dd>{intake.view.businessName}</dd>
          </div>
          {intake.view.primaryPhone ? (
            <div>
              <dt>Phone</dt>
              <dd>{intake.view.primaryPhone}</dd>
            </div>
          ) : null}
          {intake.view.primaryEmail ? (
            <div>
              <dt>Email</dt>
              <dd>{intake.view.primaryEmail}</dd>
            </div>
          ) : null}
          {intake.view.services.length > 0 ? (
            <div>
              <dt>Services</dt>
              <dd>{intake.view.services.map((s) => s.name).join(", ")}</dd>
            </div>
          ) : null}
          {intake.view.serviceAreas.length > 0 ? (
            <div>
              <dt>Service areas</dt>
              <dd>{intake.view.serviceAreas.map((a) => a.name).join(", ")}</dd>
            </div>
          ) : null}
          {intake.view.primaryEvidenceType ? (
            <div>
              <dt>Primary evidence</dt>
              <dd>{toPrimaryEvidenceTypeLabel(intake.view.primaryEvidenceType)}</dd>
            </div>
          ) : null}
          {intake.view.description ? (
            <div>
              <dt>Description</dt>
              <dd>{intake.view.description}</dd>
            </div>
          ) : null}
        </dl>
      </section>
    );
  }

  return (
    <section className="panel value-card" aria-label="Business intake">
      <p className="value-card-eyebrow">Getting started</p>
      <h2 className="value-card-headline">Tell us about your business</h2>
      <p>Share the basics your agency needs to start your website. You can submit once; your agency will follow up if anything else is needed.</p>
      <label className="field-label" htmlFor="intake-business-name">
        Business name
      </label>
      <input
        id="intake-business-name"
        value={intake.businessName}
        onChange={(event) => intake.setBusinessName(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        required
        autoComplete="organization"
      />
      <label className="field-label" htmlFor="intake-phone">
        Primary phone
      </label>
      <input
        id="intake-phone"
        value={intake.primaryPhone}
        onChange={(event) => intake.setPrimaryPhone(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        autoComplete="tel"
      />
      <label className="field-label" htmlFor="intake-email">
        Primary email
      </label>
      <input
        id="intake-email"
        type="email"
        value={intake.primaryEmail}
        onChange={(event) => intake.setPrimaryEmail(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        autoComplete="email"
      />
      <label className="field-label" htmlFor="intake-services">
        Services (one per line)
      </label>
      <textarea
        id="intake-services"
        value={intake.servicesText}
        onChange={(event) => intake.setServicesText(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        placeholder="HVAC repair&#10;Emergency service"
      />
      <label className="field-label" htmlFor="intake-service-areas">
        Service areas (one per line)
      </label>
      <textarea
        id="intake-service-areas"
        value={intake.serviceAreasText}
        onChange={(event) => intake.setServiceAreasText(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        placeholder="Austin&#10;Round Rock"
      />
      <label className="field-label" htmlFor="intake-evidence">
        Primary evidence type
      </label>
      <select
        id="intake-evidence"
        value={intake.primaryEvidenceType}
        onChange={(event) => intake.setPrimaryEvidenceType(event.target.value as PrimaryEvidenceType | "")}
        onBlur={() => intake.flushDraftOnBlur()}
      >
        <option value="">Select evidence type (optional)</option>
        {PRIMARY_EVIDENCE_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <label className="field-label" htmlFor="intake-description">
        Short description
      </label>
      <textarea
        id="intake-description"
        value={intake.description}
        onChange={(event) => intake.setDescription(event.target.value)}
        onBlur={() => intake.flushDraftOnBlur()}
        placeholder="A few sentences about your business."
      />
      <div className="action-row">
        <button
          type="button"
          className="btn-primary"
          onClick={() => void intake.submit()}
          disabled={intake.submitting || intake.businessName.trim().length === 0}
        >
          {intake.submitting ? "Submitting…" : "Submit intake"}
        </button>
      </div>
    </section>
  );
}
